import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowForwardIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  FlagIcon,
  FlagFilledIcon,
  Loader2Icon,
  PencilIcon,
  RefreshCwIcon,
  Trash2Icon,
  WandIcon,
  XIcon,
} from '../components/icons.js';
import type {
  ExtractorBatch,
  ExtractorReconciliationSummary,
  ExtractorRun,
  ExtractorVendor,
  RowDiff,
  CompareCounters,
} from '@kennisbank/shared';
import { diffPreviewRows, formatCompareCounters } from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { setVisibilityAwareInterval } from '../lib/visibility-aware-interval.js';

/**
 * Run detail — per-batch progress strip while running, paginated preview
 * table once ready, flag/refine flow, side-by-side compare with parent.
 *
 * Confidence tinting is per-row (DocAI surfaces a row scalar, not per-cell)
 * — every cell in a low-confidence row picks up the warning/error tint so
 * the operator's eye lands on the row, then scans for outliers manually.
 */

type RunWithExtras = ExtractorRun & {
  ownerDisplayName?: string;
  vendorEvidence?: string;
  vendorConfidence?: number;
};

interface PreviewRow {
  cells: Record<string, string>;
  bucket?: string;
  amount?: number;
  confidence?: number;
  description?: string;
}

interface PreviewResponse {
  runId: string;
  status: ExtractorRun['status'];
  rows: PreviewRow[];
  totalRows: number;
  offset?: number;
  limit?: number;
  vendor?: string;
  header?: Record<string, unknown>;
  grandTotalReconciliation?: string | null;
  grandTotalReconciliationDelta?: number | null;
  sidecarMissing?: boolean;
}

interface RefinementFlag {
  scope: 'cell' | 'column' | 'row';
  rowIndex?: number;
  column?: string;
  note: string;
}

const PAGE_SIZE = 50;

// Neutral chip for every vendor — the old per-brand palette used semantic
// tones decoratively (error-red Vodacom, warning-amber Paratus), which
// DESIGN.md forbids. Mirrors Extractor.tsx's vendorChipClass.
function vendorChipClass(v: ExtractorVendor | undefined): string {
  if (!v || v === 'unknown') return 'bg-nebula-gray-5 text-nebula-fg-2';
  return 'bg-nebula-tint text-nebula-secondary';
}

function isInFlight(s: ExtractorRun['status']): boolean {
  return s === 'pending' || s === 'extracting' || s === 'validating';
}

function statusPill(status: ExtractorRun['status']): JSX.Element {
  const styles: Record<ExtractorRun['status'], string> = {
    pending: 'bg-nebula-tint text-nebula-secondary',
    extracting: 'bg-nebula-tint text-nebula-secondary',
    validating: 'bg-nebula-tint text-nebula-secondary',
    ready: 'bg-nebula-success-bg text-nebula-success',
    partial: 'bg-nebula-warning-bg text-nebula-warning',
    failed: 'bg-nebula-error-bg text-nebula-error',
  };
  const labels: Record<ExtractorRun['status'], string> = {
    pending: 'Queued',
    extracting: 'Extracting',
    validating: 'Validating',
    ready: 'Ready',
    partial: 'Partial',
    failed: 'Failed',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}
    >
      {isInFlight(status) && (
        <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-nebula-secondary" />
      )}
      {labels[status]}
    </span>
  );
}

function batchPill(status: ExtractorBatch['status']): string {
  switch (status) {
    case 'ready':
      return 'bg-nebula-success-bg text-nebula-success';
    case 'failed':
      return 'bg-nebula-error-bg text-nebula-error';
    case 'running':
      return 'bg-nebula-tint text-nebula-secondary';
    default:
      return 'bg-nebula-gray-5 text-nebula-fg-2';
  }
}

function fmtCost(n?: number): string {
  if (!n || n <= 0) return '—';
  return `$${n.toFixed(2)}`;
}

/**
 * Per-row confidence -> background tint class. Mirrors the plan's
 * thresholds: <0.4 → loud (error bg), <0.7 → soft (warning bg), ≥0.7 →
 * nothing. Exported for unit testing.
 */
export function confidenceTintClass(confidence: number | undefined): string {
  if (typeof confidence !== 'number') return '';
  if (confidence < 0.4) return 'bg-nebula-error-bg';
  if (confidence < 0.7) return 'bg-nebula-warning-bg';
  return '';
}

/**
 * ZAR amount formatter for the Reconciliation panel.
 *
 * Renders "R 12,345.67" / "-R 200,182.30". Standalone (not Intl.NumberFormat
 * "ZAR") because operators want the negative sign before the currency mark,
 * and Intl puts the sign inside the prefix ("-R12,345" vs the operator-
 * expected "-R 12,345").
 */
export function formatZar(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}R ${formatted}`;
}

export function formatPercent(p: number | null | undefined): string {
  if (p === null || p === undefined || Number.isNaN(p)) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(2)}%`;
}

/** Color-band per delta magnitude. Matches the prompt's thresholds. */
export function deltaSeverityClass(
  delta: number | null | undefined,
  deltaPercent: number | null | undefined,
): { fg: string; bg: string; label: 'ok' | 'soft' | 'hard' } {
  if (delta === null || delta === undefined) {
    return { fg: 'text-nebula-fg-2', bg: '', label: 'ok' };
  }
  if (Math.abs(delta) <= 1) {
    return { fg: 'text-nebula-success', bg: 'bg-nebula-success-bg', label: 'ok' };
  }
  const pct = deltaPercent === null || deltaPercent === undefined ? null : Math.abs(deltaPercent);
  if (pct !== null && pct <= 5) {
    return { fg: 'text-nebula-warning', bg: 'bg-nebula-warning-bg', label: 'soft' };
  }
  return { fg: 'text-nebula-error', bg: 'bg-nebula-error-bg', label: 'hard' };
}

const BUCKET_TOOLTIPS: Record<string, string> = {
  OnceOffCharge: 'OnceOffCharge: one-time installation, setup, or activation fees.',
  BackBillingCost: 'BackBillingCost: prior-period charges billed in this invoice.',
  RecurringCost: 'RecurringCost: monthly subscription fees (catch-all).',
  NetworkCost: 'NetworkCost: WAN / VPN / MPLS / Metro connectivity charges.',
  OtherCost: 'OtherCost: miscellaneous fees not covered by the other buckets.',
  HardwareCost: 'HardwareCost: routers, modems, equipment line items.',
  UsageMB: 'UsageMB: metered usage (data / minutes / SMS).',
  Credit: 'Credit: negative-amount or "CR" line items reversing a charge.',
  Discounts: 'Discounts: discount or rebate line items.',
  Penalty: 'Penalty: late-payment fines / penalty fees.',
  Refunds: 'Refunds: refunded amounts.',
  Unknown: 'Unknown: bucket label missing from the row.',
};

function ReconciliationPanel({
  reconciliation,
  summary,
}: {
  reconciliation: ExtractorRun['grandTotalReconciliation'] | undefined;
  summary?: ExtractorReconciliationSummary;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Match collapses to a one-liner; everything else expands the full panel.
  if (reconciliation === 'match' && !summary) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-md border border-nebula-success/20 bg-nebula-success-bg px-3 py-2 text-sm text-nebula-success">
        <CheckCircle2Icon size={16} />
        Extracted totals reconcile with the document's stated grand total.
      </div>
    );
  }
  if (!summary) {
    // Older run that finished before reconciliationSummary shipped.
    return (
      <div className="mb-4 rounded-md border border-nebula-border-soft bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-3">
        <p className="font-semibold text-nebula-fg-2">Reconciliation</p>
        <p className="mt-0.5 text-[12px]">
          Reconciliation data not yet available — re-run the extractor for this invoice to see the
          breakdown.
        </p>
      </div>
    );
  }
  const severity = deltaSeverityClass(summary.delta, summary.deltaPercent);
  const chip =
    reconciliation === 'match'
      ? { label: 'Match', cls: 'bg-nebula-success-bg text-nebula-success' }
      : reconciliation === 'mismatch'
        ? { label: 'Mismatch', cls: 'bg-nebula-warning-bg text-nebula-warning' }
        : { label: 'Unknown', cls: 'bg-nebula-gray-5 text-nebula-fg-2' };

  const isMatch = reconciliation === 'match' || Math.abs(summary.delta) <= 1;
  if (isMatch && !expanded) {
    // Match path with summary present — short one-liner but expandable so
    // operators can still inspect per-bucket numbers when curious.
    return (
      <div className="mb-4 flex items-center justify-between gap-2 rounded-md border border-nebula-success/20 bg-nebula-success-bg px-3 py-2 text-sm text-nebula-success">
        <span className="flex items-center gap-2">
          <CheckCircle2Icon size={16} />
          Extracted totals reconcile with the document's stated grand total (
          {formatZar(summary.docaiGrandTotal)}
          ).
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[11px] font-medium text-nebula-success hover:underline"
        >
          Show details
        </button>
      </div>
    );
  }

  const perBucket = Object.entries(summary.perBucket).sort((a, b) => b[1] - a[1]);
  const totalForPct = perBucket.reduce((s, [, v]) => s + Math.abs(v), 0) || 1;

  return (
    <section
      aria-label="Reconciliation"
      className="mb-4 rounded-lg border border-nebula-border-soft bg-nebula-surface"
    >
      <header className="flex items-center justify-between gap-3 border-b border-nebula-border-soft px-3 py-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-nebula-fg-1">Reconciliation</h2>
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${chip.cls}`}
          >
            {chip.label}
          </span>
        </div>
        <span className="text-[11px] text-nebula-fg-3">
          Computed{' '}
          <span className="font-mono">{new Date(summary.computedAt).toLocaleString()}</span>
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-3">
        <div>
          <p className="ov-meta-upper text-nebula-fg-3">Stated grand total</p>
          <p className="mt-1 font-mono text-base font-semibold text-nebula-fg-1">
            {formatZar(summary.docaiGrandTotal)}
          </p>
          {summary.docaiGrandTotalSource && (
            <p className="mt-0.5 text-[11px] text-nebula-fg-3">{summary.docaiGrandTotalSource}</p>
          )}
        </div>
        <div>
          <p className="ov-meta-upper text-nebula-fg-3">Sum of buckets</p>
          <p className="mt-1 font-mono text-base font-semibold text-nebula-fg-1">
            {formatZar(summary.bucketSum)}
          </p>
          <p className="mt-0.5 text-[11px] text-nebula-fg-3">
            across {summary.rowCount} row{summary.rowCount === 1 ? '' : 's'}
          </p>
        </div>
        <div>
          <p className="ov-meta-upper text-nebula-fg-3">Delta</p>
          <p
            className={`mt-1 inline-block rounded-md px-2 py-0.5 font-mono text-base font-semibold ${severity.fg} ${severity.bg}`}
          >
            {formatZar(summary.delta)}
          </p>
          <p className={`mt-0.5 text-[11px] ${severity.fg}`}>
            {formatPercent(summary.deltaPercent)}
          </p>
        </div>
      </div>

      <div className="border-t border-nebula-border-soft px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-nebula-secondary hover:underline"
        >
          {expanded ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
          {expanded ? 'Hide per-bucket breakdown' : 'Show per-bucket breakdown'}
        </button>
        {expanded && (
          <div className="mt-2 overflow-hidden rounded-md border border-nebula-border-soft">
            <table className="w-full text-[12px]">
              <thead className="bg-nebula-gray-6 text-left">
                <tr>
                  <th className="ov-meta-upper px-2 py-1.5">Bucket</th>
                  <th className="ov-meta-upper px-2 py-1.5 text-right">Sum (R)</th>
                  <th className="ov-meta-upper px-2 py-1.5 text-right">% of bucket sum</th>
                </tr>
              </thead>
              <tbody>
                {perBucket.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-2 py-3 text-center text-nebula-fg-3">
                      No bucket data — extractor returned no rows.
                    </td>
                  </tr>
                )}
                {perBucket.map(([bucket, sum]) => {
                  const pct = (Math.abs(sum) / totalForPct) * 100;
                  return (
                    <tr
                      key={bucket}
                      className="border-t border-nebula-border-soft"
                      title={BUCKET_TOOLTIPS[bucket] ?? bucket}
                    >
                      <td className="px-2 py-1 text-nebula-fg-1">{bucket}</td>
                      <td className="px-2 py-1 text-right font-mono text-nebula-fg-1">
                        {formatZar(sum)}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-nebula-fg-3">
                        {pct.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function elapsed(startedAt?: string, finishedAt?: string): string {
  if (!startedAt) return '—';
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export function ExtractorRunPage(): JSX.Element {
  const { id: runId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // The backend 403s `updateTemplate` for anyone but kb_admin — the shared
  // template is admin-owned — so non-admins never see or send the toggle.
  const isAdmin = user?.role === 'kb_admin';
  const [run, setRun] = useState<RunWithExtras | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [refining, setRefining] = useState(false);
  const [flags, setFlags] = useState<RefinementFlag[]>([]);
  // When column-scope flags are present, this toggle (default ON) promotes
  // them into the parent template's rowsPrompt — bumps version + reactivates.
  // Cell/row-scope flags are never promoted regardless of this toggle.
  const [updateTemplate, setUpdateTemplate] = useState(true);
  const [popover, setPopover] = useState<{
    rowIndex: number;
    column: string;
    anchor: { x: number; y: number };
  } | null>(null);
  // Slice 3b — compare mode. When the run has a parentRunId, the operator
  // can toggle a dual-table view that fetches the parent's preview at the
  // same offset and diffs row-by-row using a composite TrxNumber+Description
  // +BillFrom key (refinement can drop/insert rows, so offset alone is not
  // a reliable alignment).
  const [compareMode, setCompareMode] = useState(false);
  const [parentPreview, setParentPreview] = useState<PreviewResponse | null>(null);
  const [parentLoading, setParentLoading] = useState(false);
  const parentScrollRef = useRef<HTMLDivElement | null>(null);
  const childScrollRef = useRef<HTMLDivElement | null>(null);

  const fetchRun = useCallback(async () => {
    if (!runId) return;
    try {
      const r = await apiGet<RunWithExtras>(`/api/v1/extractor/runs/${runId}`);
      setRun(r);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load run.');
    }
  }, [runId]);

  const fetchPreview = useCallback(
    async (p: number): Promise<void> => {
      if (!runId) return;
      try {
        const offset = p * PAGE_SIZE;
        const res = await apiGet<PreviewResponse>(
          `/api/v1/extractor/runs/${runId}/preview?offset=${offset}&limit=${PAGE_SIZE}`,
        );
        setPreview(res);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load preview.');
      }
    },
    [runId],
  );

  useEffect(() => {
    void fetchRun();
  }, [fetchRun]);

  // Reset per-run UI state when the route param changes (Apply &
  // re-run navigates to a new runId without unmounting this component).
  // Without this, `refining`/`flags`/`updateTemplate` survive into the
  // child run and the user sees stale banners.
  useEffect(() => {
    setRefining(false);
    setFlags([]);
    setUpdateTemplate(true);
    setError(null);
  }, [runId]);

  // Poll while in-flight, then snap to the preview load.
  useEffect(() => {
    if (!run) return;
    if (!isInFlight(run.status)) return;
    return setVisibilityAwareInterval(() => {
      void fetchRun();
    }, 2500);
  }, [run, fetchRun]);

  // Once the run reaches ready/partial, pull the preview.
  useEffect(() => {
    if (!run) return;
    if (run.status === 'ready' || run.status === 'partial') {
      void fetchPreview(page);
    }
  }, [run, page, fetchPreview]);

  // Slice 3b — parent preview fetch. Same offset/limit as the child so the
  // diff sees comparable windows; the rowMatchKey realigns where lines
  // moved. Fetch only while compare mode is on so we don't waste a request
  // on every parent-of-a-child page load.
  useEffect(() => {
    if (!compareMode || !run?.parentRunId) {
      setParentPreview(null);
      return;
    }
    let cancelled = false;
    setParentLoading(true);
    const offset = page * PAGE_SIZE;
    apiGet<PreviewResponse>(
      `/api/v1/extractor/runs/${run.parentRunId}/preview?offset=${offset}&limit=${PAGE_SIZE}`,
    )
      .then((res) => {
        if (!cancelled) setParentPreview(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load parent preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setParentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [compareMode, run?.parentRunId, page]);

  // Scroll-sync between the two compare tables. Only sync vertical scroll —
  // horizontal stays independent so the operator can inspect wide columns
  // on either side without dragging the other.
  useEffect(() => {
    if (!compareMode) return;
    const p = parentScrollRef.current;
    const c = childScrollRef.current;
    if (!p || !c) return;
    let lock = false;
    const sync = (from: HTMLDivElement, to: HTMLDivElement) => (): void => {
      if (lock) return;
      lock = true;
      to.scrollTop = from.scrollTop;
      requestAnimationFrame(() => {
        lock = false;
      });
    };
    const onP = sync(p, c);
    const onC = sync(c, p);
    p.addEventListener('scroll', onP);
    c.addEventListener('scroll', onC);
    return () => {
      p.removeEventListener('scroll', onP);
      c.removeEventListener('scroll', onC);
    };
  }, [compareMode, parentPreview, preview]);

  const compareData = useMemo<{
    diffs: RowDiff[];
    counters: CompareCounters;
    columns: string[];
  } | null>(() => {
    if (!compareMode || !parentPreview || !preview) return null;
    const { diffs, counters } = diffPreviewRows(parentPreview.rows ?? [], preview.rows ?? []);
    const colSet = new Set<string>();
    for (const r of [...(parentPreview.rows ?? []), ...(preview.rows ?? [])]) {
      for (const k of Object.keys(r.cells ?? {})) colSet.add(k);
    }
    return { diffs, counters, columns: Array.from(colSet) };
  }, [compareMode, parentPreview, preview]);

  const columns = useMemo<string[]>(() => {
    const sample = preview?.rows?.[0]?.cells;
    if (!sample) return [];
    return Object.keys(sample);
  }, [preview]);

  const onDelete = async (): Promise<void> => {
    if (!runId || !run) return;
    if (!window.confirm(`Delete the run for "${run.sourceFileName}"?`)) return;
    try {
      await apiDelete(`/api/v1/extractor/runs/${runId}`);
      navigate('/workspace/extractor');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const onDownload = async (format: 'csv' | 'xlsx'): Promise<void> => {
    if (!runId || !run) return;
    let justification: string | undefined;
    if (run.grandTotalReconciliation === 'mismatch') {
      // Pre-fill the prompt with the delta numbers so the operator isn't
      // typing from nothing. They can edit or accept.
      const s = run.reconciliationSummary;
      const prefill = s
        ? `v1 generic classifier — bucket sum ${formatZar(s.bucketSum)} differs from DocAI total ${formatZar(s.docaiGrandTotal)} by ${formatPercent(s.deltaPercent)}. Manual review required before OneView upload.`
        : '';
      const v = window.prompt(
        'Reconciliation is a mismatch. Add a short justification (required) to download:',
        prefill,
      );
      if (!v || !v.trim()) return;
      justification = v.trim();
    }
    try {
      const qs = justification ? `?downloadJustification=${encodeURIComponent(justification)}` : '';
      const res = await apiGet<{ url: string }>(
        `/api/v1/extractor/runs/${runId}/download/${format}${qs}`,
      );
      window.location.href = res.url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Download failed.');
    }
  };

  const addFlag = (flag: RefinementFlag): void => {
    setFlags((cur) => [...cur, flag]);
    setPopover(null);
  };

  const onApplyRefinement = async (): Promise<void> => {
    if (!runId || flags.length === 0) return;
    setRefining(true);
    try {
      const notes = flags
        .map((f) => {
          if (f.scope === 'cell') {
            return `Cell row ${(f.rowIndex ?? 0) + 1} col "${f.column}": ${f.note}`;
          }
          if (f.scope === 'column') {
            return `Column "${f.column}" (all rows): ${f.note}`;
          }
          return `Row ${(f.rowIndex ?? 0) + 1} (all cells): ${f.note}`;
        })
        .join('\n');
      // Structured column-scope flags for optional template promotion.
      const columnFlags = flags
        .filter(
          (f): f is RefinementFlag & { column: string } =>
            f.scope === 'column' && typeof f.column === 'string' && f.column.length > 0,
        )
        .map((f) => ({ column: f.column, note: f.note }));
      const res = await apiPost<{ runId: string }>(`/api/v1/extractor/runs/${runId}/refine`, {
        notes,
        ...(columnFlags.length > 0 ? { columnFlags } : {}),
        ...(columnFlags.length > 0 && isAdmin && updateTemplate ? { updateTemplate: true } : {}),
      });
      navigate(`/workspace/extractor/runs/${res.runId}`);
    } catch (err) {
      setRefining(false);
      setError(err instanceof ApiError ? err.message : 'Refinement failed.');
    }
  };

  const columnFlagCount = useMemo(() => flags.filter((f) => f.scope === 'column').length, [flags]);

  const isCellFlagged = (rowIndex: number, column: string): boolean =>
    flags.some(
      (f) =>
        (f.scope === 'cell' && f.rowIndex === rowIndex && f.column === column) ||
        (f.scope === 'column' && f.column === column) ||
        (f.scope === 'row' && f.rowIndex === rowIndex),
    );

  if (error && !run) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-10">
          <p className="rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            {error}
          </p>
          <Link
            to="/workspace/extractor"
            className="mt-3 inline-flex items-center gap-1 text-sm text-nebula-secondary hover:underline"
          >
            ← Back to Data Extractor
          </Link>
        </div>
      </Layout>
    );
  }

  if (!run) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-10">
          <div className="inline-flex items-center gap-2 text-sm text-nebula-fg-3">
            <Loader2Icon size={14} className="animate-spin" />
            Loading run…
          </div>
        </div>
      </Layout>
    );
  }

  const totalPages = preview ? Math.max(1, Math.ceil(preview.totalRows / PAGE_SIZE)) : 1;
  const finishedAt = (run as ExtractorRun & { finishedAt?: string }).finishedAt;
  const startedAt = (run as ExtractorRun & { startedAt?: string }).startedAt;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-6 py-6">
        <Link
          to="/workspace/extractor"
          className="mb-3 inline-flex items-center gap-1 text-[12px] text-nebula-secondary hover:underline"
        >
          ← Data Extractor
        </Link>

        {run.parentRunId && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-nebula-border-soft bg-nebula-tint px-3 py-2 text-[12px] text-nebula-secondary">
            <div className="flex items-center gap-2">
              <WandIcon size={14} />
              <span>
                This is a refinement of{' '}
                <Link
                  to={`/workspace/extractor/runs/${run.parentRunId}`}
                  className="font-medium underline"
                >
                  parent run
                </Link>
                .
              </span>
            </div>
            {(run.status === 'ready' || run.status === 'partial') && (
              <button
                type="button"
                onClick={() => setCompareMode((v) => !v)}
                aria-pressed={compareMode}
                className={`rounded-md border px-2.5 py-1 text-[12px] font-medium ${
                  compareMode
                    ? 'border-nebula-secondary bg-nebula-secondary text-white hover:bg-nebula-primary'
                    : 'border-nebula-border bg-nebula-surface text-nebula-fg-1 hover:bg-nebula-tint'
                }`}
              >
                {compareMode ? 'Exit compare' : 'Compare with parent'}
              </button>
            )}
          </div>
        )}

        <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="break-all text-xl font-semibold text-nebula-fg-1">
              {run.sourceFileName}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${vendorChipClass(
                  run.vendor,
                )}`}
              >
                {run.vendor}
              </span>
              {statusPill(run.status)}
              <span className="text-[11px] text-nebula-fg-3">
                Elapsed: <span className="font-mono">{elapsed(startedAt, finishedAt)}</span>
              </span>
              <span className="text-[11px] text-nebula-fg-3">
                Cost: <span className="font-mono">{fmtCost(run.costUsd)}</span>
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {(run.status === 'ready' || run.status === 'partial') && (
              <>
                <button
                  type="button"
                  onClick={() => void onDownload('csv')}
                  className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary"
                >
                  <DownloadIcon size={14} />
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={() => void onDownload('xlsx')}
                  className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-sm text-nebula-fg-1 hover:bg-nebula-tint"
                >
                  <DownloadIcon size={14} />
                  XLSX
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => void onDelete()}
              aria-label="Delete run"
              className="rounded-md border border-nebula-border bg-nebula-surface p-1.5 text-nebula-fg-3 hover:bg-nebula-error-bg hover:text-nebula-error"
            >
              <Trash2Icon size={14} />
            </button>
          </div>
        </header>

        {error && (
          <p
            role="alert"
            className="mb-4 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error"
          >
            {error}
          </p>
        )}

        {run.status === 'failed' && run.errorMessage && (
          <div className="mb-4 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            <span className="font-semibold">Failed:</span> {run.errorMessage}
          </div>
        )}

        {(run.status === 'ready' || run.status === 'partial') && (
          <ReconciliationPanel
            reconciliation={run.grandTotalReconciliation}
            summary={run.reconciliationSummary}
          />
        )}

        {/* Per-batch progress strip */}
        {run.batches && run.batches.length > 0 && (
          <section className="mb-5 rounded-lg border border-nebula-border-soft bg-nebula-surface p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-nebula-fg-1">Batches</h2>
              <span className="text-[11px] text-nebula-fg-3">
                {run.batches.length} batch{run.batches.length === 1 ? '' : 'es'}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {run.batches.map((b) => (
                <div
                  key={b.id}
                  title={
                    b.errorMessage
                      ? `Pages ${b.pageRangeStart}-${b.pageRangeEnd}: ${b.errorMessage}`
                      : `Pages ${b.pageRangeStart}-${b.pageRangeEnd} · ${b.status} · ${fmtCost(b.costUsd)}`
                  }
                  className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-medium ${batchPill(b.status)}`}
                >
                  <span className="font-mono">
                    {b.pageRangeStart}–{b.pageRangeEnd}
                  </span>
                  <span className="opacity-70">·</span>
                  <span>{b.status}</span>
                  {b.costUsd > 0 && (
                    <>
                      <span className="opacity-70">·</span>
                      <span className="font-mono">{fmtCost(b.costUsd)}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Refinement flag bar */}
        {flags.length > 0 && (
          <div className="sticky top-2 z-30 mb-4 flex items-center justify-between gap-3 rounded-lg border border-nebula-secondary/40 bg-nebula-tint px-3 py-2 shadow-sm">
            <div className="flex items-center gap-2 text-sm text-nebula-fg-1">
              <FlagFilledIcon size={14} className="text-nebula-secondary" />
              <span>
                {flags.length} flag{flags.length === 1 ? '' : 's'} ready to apply
              </span>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && columnFlagCount > 0 && (
                <label
                  className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-nebula-fg-2"
                  title="Promote column-scope rules into the template (bumps version + activates). Cell/row flags are never promoted."
                >
                  <input
                    type="checkbox"
                    checked={updateTemplate}
                    onChange={(e) => setUpdateTemplate(e.target.checked)}
                    disabled={refining}
                    className="accent-nebula-primary"
                  />
                  Also update template ({columnFlagCount} column rule
                  {columnFlagCount === 1 ? '' : 's'})
                </label>
              )}
              <button
                type="button"
                onClick={() => setFlags([])}
                disabled={refining}
                className="text-[12px] text-nebula-fg-3 hover:text-nebula-fg-1"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => void onApplyRefinement()}
                disabled={refining}
                className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
              >
                {refining ? (
                  <Loader2Icon size={14} className="animate-spin" />
                ) : (
                  <RefreshCwIcon size={14} />
                )}
                Apply {flags.length} flag{flags.length === 1 ? '' : 's'} & re-run
              </button>
            </div>
          </div>
        )}

        {/* Compare counter strip */}
        {compareMode && compareData && (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-nebula-border-soft bg-nebula-surface px-3 py-2 text-[12px]">
            <span className="font-semibold text-nebula-fg-1">Differences</span>
            <span className="font-mono text-nebula-fg-2">
              {formatCompareCounters(compareData.counters)}
            </span>
            <span className="ml-auto text-[11px] text-nebula-fg-3">
              Matched by TrxNumber + Description + BillFrom
            </span>
          </div>
        )}
        {compareMode && parentLoading && !compareData && (
          <p className="mb-3 inline-flex items-center gap-2 text-[12px] text-nebula-fg-3">
            <Loader2Icon size={12} className="animate-spin" />
            Loading parent preview…
          </p>
        )}

        {/* Compare-mode dual table */}
        {compareMode && compareData && (run.status === 'ready' || run.status === 'partial') && (
          <CompareDualTable
            diffs={compareData.diffs}
            columns={compareData.columns}
            parentScrollRef={parentScrollRef}
            childScrollRef={childScrollRef}
          />
        )}

        {/* Preview table */}
        {!compareMode && (run.status === 'ready' || run.status === 'partial') && (
          <section className="rounded-lg border border-nebula-border-soft bg-nebula-surface">
            <div className="flex items-center justify-between border-b border-nebula-border-soft px-3 py-2">
              <div className="text-sm font-semibold text-nebula-fg-1">
                Extracted rows{' '}
                {preview && (
                  <span className="ml-1 text-[12px] font-normal text-nebula-fg-3">
                    {preview.totalRows} total
                  </span>
                )}
              </div>
              {preview && totalPages > 1 && (
                <div className="flex items-center gap-2 text-[12px]">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded border border-nebula-border bg-nebula-surface px-2 py-0.5 text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-40"
                  >
                    ‹ Prev
                  </button>
                  <span className="font-mono">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page + 1 >= totalPages}
                    className="rounded border border-nebula-border bg-nebula-surface px-2 py-0.5 text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-40"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>

            {preview?.sidecarMissing && (
              <p className="px-3 py-4 text-sm text-nebula-fg-3">
                The extracted JSON sidecar is not in storage yet. Re-run the job from the list page
                if this run finished before the sidecar fix landed.
              </p>
            )}

            {preview && !preview.sidecarMissing && preview.rows.length === 0 && (
              <p className="px-3 py-4 text-sm text-nebula-fg-3">No rows on this page.</p>
            )}

            {preview && preview.rows.length > 0 && (
              <div className="thin-scroll max-h-[640px] overflow-auto">
                <table className="w-full min-w-[1200px] text-[12px]">
                  <thead className="sticky top-0 z-10 bg-nebula-gray-6 text-left">
                    <tr>
                      <th className="ov-meta-upper sticky left-0 z-20 bg-nebula-gray-6 px-2 py-1.5 text-right">
                        #
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c}
                          className="ov-meta-upper whitespace-nowrap border-l border-nebula-border-soft px-2 py-1.5"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => {
                      const absoluteIndex = (preview.offset ?? page * PAGE_SIZE) + i;
                      const tint = confidenceTintClass(row.confidence);
                      return (
                        <tr
                          key={absoluteIndex}
                          className={`border-t border-nebula-border-soft ${tint}`}
                        >
                          <td className="sticky left-0 z-10 bg-inherit px-2 py-1 text-right font-mono text-[11px] text-nebula-fg-3">
                            {absoluteIndex + 1}
                          </td>
                          {columns.map((c) => {
                            const v = row.cells[c] ?? '';
                            const flagged = isCellFlagged(absoluteIndex, c);
                            return (
                              <td
                                key={c}
                                className="group relative max-w-[260px] truncate border-l border-nebula-border-soft px-2 py-1 align-top"
                                title={v}
                              >
                                <span
                                  className={v === '' ? 'text-nebula-fg-4' : 'text-nebula-fg-1'}
                                >
                                  {v === '' ? '—' : v}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setPopover({
                                      rowIndex: absoluteIndex,
                                      column: c,
                                      anchor: { x: rect.right, y: rect.bottom },
                                    });
                                  }}
                                  aria-label={`Flag cell row ${absoluteIndex + 1} column ${c}`}
                                  className={`absolute right-1 top-1 hidden rounded p-0.5 group-hover:inline-flex group-focus-within:inline-flex ${
                                    flagged
                                      ? 'inline-flex text-nebula-secondary'
                                      : 'text-nebula-fg-4 hover:text-nebula-secondary'
                                  }`}
                                >
                                  {flagged ? (
                                    <FlagFilledIcon size={11} />
                                  ) : (
                                    <PencilIcon size={11} />
                                  )}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Empty/in-flight states */}
        {run.status === 'pending' && (
          <p className="rounded-md border border-nebula-border-soft bg-nebula-surface px-3 py-6 text-center text-sm text-nebula-fg-3">
            Run is queued. Polling will pick it up automatically.
          </p>
        )}
        {isInFlight(run.status) && run.status !== 'pending' && (
          <p className="rounded-md border border-nebula-border-soft bg-nebula-surface px-3 py-6 text-center text-sm text-nebula-fg-3">
            <span className="inline-flex items-center gap-2">
              <Loader2Icon size={14} className="animate-spin" />
              {run.status === 'extracting'
                ? 'Extracting line items…'
                : 'Validating extracted rows…'}
            </span>
          </p>
        )}
      </div>

      {popover && (
        <FlagPopover
          rowIndex={popover.rowIndex}
          column={popover.column}
          anchor={popover.anchor}
          onClose={() => setPopover(null)}
          onAdd={addFlag}
        />
      )}
    </Layout>
  );
}

// ----- Flag popover --------------------------------------------------------

function FlagPopover({
  rowIndex,
  column,
  anchor,
  onClose,
  onAdd,
}: {
  rowIndex: number;
  column: string;
  anchor: { x: number; y: number };
  onClose: () => void;
  onAdd: (flag: RefinementFlag) => void;
}): JSX.Element {
  const [scope, setScope] = useState<'cell' | 'column' | 'row'>('cell');
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const submit = (): void => {
    const trimmed = note.trim();
    if (!trimmed) return;
    const flag: RefinementFlag = {
      scope,
      note: trimmed.slice(0, 500),
      ...(scope === 'cell' ? { rowIndex, column } : {}),
      ...(scope === 'column' ? { column } : {}),
      ...(scope === 'row' ? { rowIndex } : {}),
    };
    onAdd(flag);
  };

  // Clamp to viewport so the popover stays fully on-screen on all sides.
  // Initial estimate uses a conservative height; once mounted, useLayoutEffect
  // re-clamps using the popover's actual measured size.
  const [pos, setPos] = useState<{ left: number; top: number }>(() => ({
    left: Math.max(8, Math.min(anchor.x + 6, window.innerWidth - 328)),
    top: Math.max(8, Math.min(anchor.y + 6, window.innerHeight - 400)),
  }));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPos({
      left: Math.max(8, Math.min(anchor.x + 6, window.innerWidth - w - 8)),
      top: Math.max(8, Math.min(anchor.y + 6, window.innerHeight - h - 8)),
    });
  }, [anchor.x, anchor.y]);

  const { left, top } = pos;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Flag cell for refinement"
      className="fixed z-50 w-[320px] rounded-lg border border-nebula-border bg-nebula-surface p-3 shadow-xl"
      style={{ left, top, boxShadow: 'var(--ov-shadow-popover, 0 10px 24px rgba(14,12,20,0.18))' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-nebula-fg-1">
          <FlagIcon size={12} className="text-nebula-secondary" />
          Flag for refinement
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-1 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
        >
          <XIcon size={12} />
        </button>
      </div>

      <div className="mb-2 text-[11px] text-nebula-fg-3">
        Row {rowIndex + 1} · Column <span className="font-mono">{column}</span>
      </div>

      <div className="mb-2 space-y-1">
        <ScopeRadio
          checked={scope === 'cell'}
          onChange={() => setScope('cell')}
          label="Just this cell"
          desc={`Fix only row ${rowIndex + 1}'s ${column}`}
        />
        <ScopeRadio
          checked={scope === 'column'}
          onChange={() => setScope('column')}
          label={`This column for every row`}
          desc={`Always parse ${column} this way`}
        />
        <ScopeRadio
          checked={scope === 'row'}
          onChange={() => setScope('row')}
          label="This row globally"
          desc="Re-derive every cell in this row"
        />
      </div>

      <textarea
        ref={inputRef}
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder="Refinement instruction (≤500 chars)"
        rows={3}
        className="w-full resize-none rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-[12px] text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
      />
      <div className="mt-1 flex items-center justify-between text-[10px] text-nebula-fg-4">
        <span>{note.length}/500</span>
      </div>

      <div className="mt-2 flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-1 text-[12px] text-nebula-fg-2 hover:bg-nebula-tint"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={note.trim().length === 0}
          className="inline-flex items-center gap-1 rounded-md bg-nebula-primary px-3 py-1 text-[12px] font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
        >
          <ArrowForwardIcon size={11} />
          Add flag
        </button>
      </div>
    </div>
  );
}

function ScopeRadio({
  checked,
  onChange,
  label,
  desc,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  desc: string;
}): JSX.Element {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2 rounded-md border px-2 py-1.5 text-[12px] ${
        checked
          ? 'border-nebula-secondary bg-nebula-tint'
          : 'border-nebula-border-soft hover:bg-nebula-tint'
      }`}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 accent-nebula-primary"
      />
      <span>
        <span className="block font-medium text-nebula-fg-1">{label}</span>
        <span className="block text-[10px] text-nebula-fg-3">{desc}</span>
      </span>
    </label>
  );
}

// ----- Compare-mode dual table --------------------------------------------

/**
 * Side-by-side preview. Each diff entry renders TWO <tr>s wrapped in
 * separate <table>s — one in the parent scroller, one in the child
 * scroller. We pad whichever side is missing so the rows line up
 * vertically and the scroll-sync stays honest.
 */
function CompareDualTable({
  diffs,
  columns,
  parentScrollRef,
  childScrollRef,
}: {
  diffs: RowDiff[];
  columns: string[];
  parentScrollRef: { current: HTMLDivElement | null };
  childScrollRef: { current: HTMLDivElement | null };
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3">
      <CompareTable side="parent" diffs={diffs} columns={columns} scrollRef={parentScrollRef} />
      <CompareTable side="child" diffs={diffs} columns={columns} scrollRef={childScrollRef} />
    </div>
  );
}

function rowClassForDiff(
  side: 'parent' | 'child',
  d: RowDiff,
): { className: string; marker: string | null } {
  if (d.classification === 'removed') {
    return side === 'parent'
      ? {
          className: 'bg-nebula-gray-5 text-nebula-fg-3 line-through',
          marker: '(removed)',
        }
      : { className: 'opacity-30', marker: null };
  }
  if (d.classification === 'new') {
    return side === 'child'
      ? {
          className: 'bg-nebula-success-bg/60',
          marker: '(new)',
        }
      : { className: 'opacity-30', marker: null };
  }
  if (d.classification === 'value-changed') {
    return side === 'child'
      ? {
          className: 'bg-nebula-info-bg/60 ring-2 ring-inset ring-nebula-secondary',
          marker: null,
        }
      : { className: '', marker: null };
  }
  return { className: '', marker: null };
}

function CompareTable({
  side,
  diffs,
  columns,
  scrollRef,
}: {
  side: 'parent' | 'child';
  diffs: RowDiff[];
  columns: string[];
  scrollRef: { current: HTMLDivElement | null };
}): JSX.Element {
  return (
    <section className="rounded-lg border border-nebula-border-soft bg-nebula-surface">
      <div className="border-b border-nebula-border-soft bg-nebula-gray-6 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-nebula-fg-2">
          {side === 'parent' ? 'Parent run' : 'This (child) run'}
        </h3>
      </div>
      <div ref={scrollRef} className="thin-scroll max-h-[640px] overflow-auto">
        <table className="w-full min-w-[700px] text-[12px]">
          <thead className="sticky top-0 z-10 bg-nebula-gray-6 text-left">
            <tr>
              <th className="ov-meta-upper sticky left-0 z-20 bg-nebula-gray-6 px-2 py-1.5 text-right">
                #
              </th>
              {columns.map((c) => (
                <th
                  key={c}
                  className="ov-meta-upper whitespace-nowrap border-l border-nebula-border-soft px-2 py-1.5"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diffs.map((d, i) => {
              const row = side === 'parent' ? d.parent : d.child;
              const { className, marker } = rowClassForDiff(side, d);
              const showConfidenceDot = d.classification === 'confidence-only';
              const diffTitle =
                d.classification === 'value-changed' && d.changedColumns.length > 0
                  ? `Changed columns: ${d.changedColumns.join(', ')}`
                  : undefined;
              return (
                <tr
                  key={`${side}-${d.key}-${i}`}
                  className={`border-t border-nebula-border-soft ${className}`}
                  title={diffTitle}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-2 py-1 text-right font-mono text-[11px] text-nebula-fg-3">
                    {showConfidenceDot && (
                      <span
                        title="Confidence-only change"
                        className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-nebula-secondary align-middle"
                      />
                    )}
                    {i + 1}
                    {marker && (
                      <span className="ml-1 text-[10px] font-medium text-nebula-fg-3">
                        {marker}
                      </span>
                    )}
                  </td>
                  {columns.map((c) => {
                    const v = row?.cells?.[c] ?? '';
                    const isChangedCol =
                      d.classification === 'value-changed' && d.changedColumns.includes(c);
                    return (
                      <td
                        key={c}
                        className={`max-w-[260px] truncate border-l border-nebula-border-soft px-2 py-1 align-top ${
                          isChangedCol ? 'font-semibold text-nebula-fg-1' : ''
                        }`}
                        title={v}
                      >
                        {row ? (
                          <span className={v === '' ? 'text-nebula-fg-4' : 'text-nebula-fg-1'}>
                            {v === '' ? '—' : v}
                          </span>
                        ) : (
                          <span className="text-nebula-fg-4">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {diffs.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-3 py-6 text-center text-[12px] text-nebula-fg-3"
                >
                  No rows on this page.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
